// src/pages/Login.js
import React, { useEffect, useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { auth, db, googleProvider, signWith, resolveRedirectResult } from "../firebaseConfig";
import { signInWithEmailAndPassword } from "firebase/auth";
import { collection, query, where, getDocs } from "firebase/firestore";
import { ensureUserProfile, PLAN_LIMITS } from "../services/userService";
import { ALWAYS_PREMIUM } from "../config/featureFlags";
import { createPremiumCheckoutSession } from "../services/subscriptionService";

const Login = () => {
  const [emailOrUsername, setEmailOrUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const wantsPremium = params.get("plan") === "premium";
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);
  const [redirectChecked, setRedirectChecked] = useState(false);

  useEffect(() => {
    // Éviter les appels multiples (React StrictMode en dev appelle 2 fois)
    if (redirectChecked) {
      if (process.env.NODE_ENV !== 'production') console.log("Redirect already checked, skipping");
      return;
    }
    
    setRedirectChecked(true);
    if (process.env.NODE_ENV !== 'production') console.log("Checking for redirect result (Login)...");
    
    resolveRedirectResult()
      .then(async (res) => {
        if (process.env.NODE_ENV !== 'production') console.log("Redirect result (Login):", res);
        if (res && res.user) {
          if (process.env.NODE_ENV !== 'production') console.log("User found from redirect:", res.user.email);
          setSuccess("Connexion en cours...");
          await ensureUserProfile(res.user);
          if (wantsPremium) {
            await startPremiumCheckout(res.user, "oauth_redirect");
          } else {
            setSuccess("Connexion réussie ! Redirection…");
            setTimeout(() => navigate("/dashboard"), 600);
          }
        } else {
          if (process.env.NODE_ENV !== 'production') console.log("No redirect result found (Login)");
        }
      })
      .catch((e) => {
        if (process.env.NODE_ENV !== 'production') console.error("Redirect error (Login):", e);
        setError(mapAuthError(e));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      let email = emailOrUsername;

      if (!emailOrUsername.includes("@")) {
        const q = query(collection(db, "users"), where("username", "==", emailOrUsername));
        const snap = await getDocs(q);
        if (snap.empty) throw new Error("Nom d'utilisateur introuvable");
        email = snap.docs[0].data().email;
      }

      const credential = await signInWithEmailAndPassword(auth, email, password);
      // Ne pas forcer le premium - ensureUserProfile ne modifiera pas le plan existant
      await ensureUserProfile(credential.user);
      if (!ALWAYS_PREMIUM && wantsPremium) {
        await startPremiumCheckout(credential.user, "email_login");
      } else {
        setSuccess("Connexion réussie ! Redirection…");
        setTimeout(() => navigate("/dashboard"), 800);
      }
    } catch (err) {
      setError(mapAuthError(err));
    }
  };

  // === Socials ===
  const signGoogle = async () => {
    setError(""); setSuccess("");
    try {
      const res = await signWith(googleProvider);
      if (res?.user) {
        await ensureUserProfile(res.user);
        if (wantsPremium) {
          await startPremiumCheckout(res.user, "oauth_google");
        } else {
          setSuccess("Connecté avec Google !");
          setTimeout(() => navigate("/dashboard"), 600);
        }
      }
    } catch (e) { setError(mapAuthError(e)); }
  };


  const startPremiumCheckout = async (user, source = "upgrade_login") => {
    try {
      setError("");
      setSuccess("Redirection vers Stripe…");
      setIsStartingCheckout(true);
      const url = await createPremiumCheckoutSession({
        user,
        metadata: { source },
      });
      window.location.href = url;
    } catch (checkoutError) {
      console.error("Erreur lors du lancement du checkout Stripe :", checkoutError);
      setIsStartingCheckout(false);
      setError(
        checkoutError?.message ||
          "Impossible de lancer le paiement. Vérifiez votre connexion et réessayez."
      );
    }
  };

  return (
    <div className="min-h-screen bg-[#EAEEFE] px-4 flex flex-col md:flex-row md:items-center md:justify-center relative pb-20 md:pb-0 pt-24 md:pt-0">
      <Link to="/">
        <header className="absolute top-4 left-4 flex items-center space-x-2 z-10">
          <img src="/Logo1.png" alt="DressCode Logo" className="h-10 md:h-12" />
          <img src="/Logo2.png" alt="DressCode Subtitle Logo" className="h-3 md:h-4" />
        </header>
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-2 items-start md:items-center justify-center max-w-[70rem] w-full gap-8 md:gap-24 px-4 relative mt-12 md:mt-0">
        <div className="flex flex-col justify-center max-w-md w-full mx-auto text-center md:text-left">
          <h1 className="text-[32px] sm:text-[40px] md:text-[50px] font-semibold text-gray-800 leading-tight mb-3 md:mb-4 text-center md:text-left">
            Connectez-vous
          </h1>
          <p className="text-[20px] sm:text-[24px] md:text-[35px] font-medium text-gray-600 mb-4 md:mb-6 text-center md:text-left">
            Accédez à votre garde-robe personnalisée en un clic.
          </p>
          <p className="text-[16px] text-gray-600 text-center md:text-left relative z-10">
            Vous n'avez pas de compte ?
            <br />
            <Link to="/Signup" className="text-[16px] text-[#4D47C3] font-semibold hover:underline">
              Créez-en un ici !
            </Link>
          </p>
        </div>

        <div className="w-full md:w-full md:p-16 mx-auto md:mx-0">
          <h2 className="text-[24px] md:text-[30px] font-medium text-gray-800 mb-4 md:mb-6 text-left">Connexion</h2>
          {error && <p className="text-red-500 mb-4">{error}</p>}
          {success && <p className="text-green-600 mb-4">{success}</p>}

          <form className="space-y-4 md:space-y-6" onSubmit={handleLogin}>
            <div className="relative shadow-lg" style={{ boxShadow: "0px 24px 10px rgba(36, 38, 43, 0.06)" }}>
              <input
                type="text"
                placeholder="Email ou nom d'utilisateur"
                value={emailOrUsername}
                onChange={(e) => setEmailOrUsername(e.target.value)}
                className="w-full md:w-[369px] h-[52px] md:h-[56px] px-4 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none placeholder-violet text-base md:text-lg"
              />
            </div>

            <div className="relative shadow-lg" style={{ boxShadow: "0px 24px 10px rgba(36, 38, 43, 0.06)" }}>
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full md:w-[369px] h-[52px] md:h-[56px] px-4 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none placeholder-violet text-base md:text-lg"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A7A3FF]">
                {showPassword ? <FaEyeSlash className="h-5 w-5" /> : <FaEye className="h-5 w-5" />}
              </button>
            </div>

            <div className="text-right">
              <a href="/forgot-password" className="text-[13px] text-[#7a7a7a] hover:underline">
                Mot de passe oublié ?
              </a>
            </div>

            <button
              type="submit"
              disabled={isStartingCheckout}
              className="w-full md:w-[369px] h-[52px] md:h-[56px] bg-black text-white rounded-lg shadow-lg hover:bg-gray-800 disabled:opacity-70 disabled:cursor-not-allowed text-base md:text-lg"
            >
              {isStartingCheckout ? "Redirection..." : "Se connecter"}
            </button>
          </form>

          {/* Google Sign In */}
          <div className="flex items-center my-6">
            <div className="flex-grow h-px bg-gray-300"></div>
            <span className="px-4 text-gray-500 text-sm">ou continuez avec</span>
            <div className="flex-grow h-px bg-gray-300"></div>
          </div>

          <div className="flex justify-center">
            <button
              onClick={signGoogle}
              disabled={isStartingCheckout}
              className="w-full md:w-[369px] h-[52px] md:h-[56px] flex items-center justify-center gap-3 bg-white border-2 border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 hover:border-gray-400 transition-all disabled:opacity-60 disabled:cursor-not-allowed text-base md:text-lg"
              aria-label="Continuer avec Google"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span className="text-gray-700 font-medium">Continuer avec Google</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function mapAuthError(err) {
  if (!err) return "Erreur de connexion";
  
  // Log l'erreur pour le debug (visible dans la console uniquement)
  console.error("Auth error:", err.code, err.message);
  
  switch (err.code) {
    case "auth/user-not-found":
      return "Aucun compte associé à cet identifiant";
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Identifiants incorrects";
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "Connexion annulée";
    case "auth/popup-blocked":
      return "Pop-up bloquée. Autorisez les pop-ups pour ce site";
    case "auth/account-exists-with-different-credential":
      return "Un compte existe déjà avec un autre mode de connexion";
    case "auth/network-request-failed":
      return "Problème de connexion. Vérifiez votre internet";
    case "auth/too-many-requests":
      return "Trop de tentatives. Réessayez plus tard";
    case "auth/invalid-email":
      return "Adresse email invalide";
    case "auth/email-already-in-use":
      return "Cet email est déjà utilisé";
    case "auth/weak-password":
      return "Le mot de passe doit faire au moins 6 caractères";
    case "auth/operation-not-allowed":
      return "Méthode de connexion non disponible";
    case "auth/user-disabled":
      return "Ce compte a été désactivé";
    default:
      // Ne JAMAIS afficher err.message (trop technique)
      return "Une erreur s'est produite. Veuillez réessayer";
  }
}

export default Login;
